import { type AuthUser, useAuthStore } from '@/lib/auth/store'
import { env } from '@/lib/env'
import { ApiError } from './errors'
import type { ApiErrorCode } from './errors'

interface ApiClientOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  // Si true, no se adjunta Authorization: Bearer (para login, register, etc.)
  skipAuth?: boolean
  // Si true, no se reintenta tras un 401 (evita bucle infinito en el refresh)
  skipRefreshOnUnauthorized?: boolean
}

// Mutex global para evitar múltiples refreshes simultáneos.
// Si varias peticiones fallan con 401 al mismo tiempo, solo una llama a /token/refresh.
let refreshInFlight: Promise<void> | null = null

export const apiClient = async <T>(path: string, options: ApiClientOptions = {}): Promise<T> => {
  const { body, skipAuth, skipRefreshOnUnauthorized, headers, ...rest } = options

  const accessToken = useAuthStore.getState().accessToken
  const isFormData = body instanceof FormData
  const finalHeaders: Record<string, string> = {
    'X-Abakoo-Key': env.VITE_ABAKOO_API_KEY,
    // Content-Type solo cuando hay body — los GET sin body no deben enviarlo
    // (FastEndpoints intenta deserializar JSON si ve Content-Type: application/json, aunque el body sea vacío)
    ...(isFormData || body === undefined ? {} : { 'Content-Type': 'application/json' }),
    ...(headers as Record<string, string> | undefined),
  }
  if (!skipAuth && accessToken) {
    finalHeaders.Authorization = `Bearer ${accessToken}`
  }

  // Construir fetchInit sin body cuando es undefined (exactOptionalPropertyTypes)
  const fetchInit: RequestInit = {
    ...rest,
    headers: finalHeaders,
    credentials: 'include', // Siempre — necesario para que la cookie abk_rt viaje
  }
  if (body !== undefined) {
    fetchInit.body = isFormData ? body : JSON.stringify(body)
  }

  const response = await fetch(`${env.VITE_API_URL}${path}`, fetchInit)

  // Interceptor 401: si el access token expiró, hace silent refresh y reintenta
  if (response.status === 401 && !skipRefreshOnUnauthorized && !skipAuth) {
    refreshInFlight ??= refreshAccessToken()
    try {
      await refreshInFlight
    } finally {
      refreshInFlight = null
    }
    return apiClient<T>(path, { ...options, skipRefreshOnUnauthorized: true })
  }

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as Record<string, unknown>
    throw new ApiError(
      response.status,
      (errorBody.errorCode as ApiErrorCode | undefined) ?? 'UNKNOWN',
      (errorBody.detail as string | undefined) ??
        (errorBody.title as string | undefined) ??
        response.statusText,
      errorBody.correlationId as string | undefined,
      errorBody,
    )
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

// Exportada para que guards.ts pueda usarla en el silent refresh del beforeLoad.
// Usa fetch directo (no apiClient) para evitar recursión.
// Tope duro del silent refresh. Esta petición corre DENTRO del beforeLoad de los guards,
// así que mientras no resuelva el router no renderiza nada: sin tope, una API que acepta la
// conexión y nunca responde (cold start de Container Apps, red estancada, proxy colgado) deja
// al usuario en el spinner para siempre, sin forma de llegar al login. 8 s es holgado frente al
// rango observado de 300–800 ms.
const REFRESH_TIMEOUT_MS = 8_000

export const refreshAccessToken = async (): Promise<void> => {
  let response: Response
  try {
    response = await fetch(`${env.VITE_API_URL}/api/v1/auth/token/refresh`, {
      method: 'POST',
      headers: { 'X-Abakoo-Key': env.VITE_ABAKOO_API_KEY },
      credentials: 'include',
      signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
    })
  } catch (cause) {
    // Fallo de red, CORS o timeout: NO se limpia el store. Un corte de red no invalida la
    // sesión, y borrar el token en memoria obligaría a reautenticar por un problema
    // transitorio. Los guards atrapan este throw y caen al login, que es la salida correcta
    // cuando el refresh es lo único que podía restaurar la sesión.
    throw new ApiError(0, 'REFRESH_FAILED', 'No se pudo contactar a la API', undefined, {
      cause: cause instanceof Error ? cause.name : String(cause),
    })
  }

  if (!response.ok) {
    useAuthStore.getState().clear()
    throw new ApiError(response.status, 'REFRESH_FAILED', 'Refresh token inválido o expirado')
  }

  const data = (await response.json()) as {
    accessToken: string
    expiresAt: string
    user: AuthUser
  }
  useAuthStore.getState().setAuth(data.accessToken, data.expiresAt, data.user)
}
